import _ from 'lodash'
import $ from 'cash-dom'
import {fmt, s, greyscale, palette, rewind, ffwd, cancel, offset, sfc32, quadMinMax, bezierMinMax} from './util.js'

export default class Loop{
  constructor({book, angles, radii, origin, legend}){
    this.book = book
    this.layout = Loop.layout(book.stats, {angles, radii, origin})
    this.legend = legend
  }

  attach(dom){
    this.dom = $(dom).find('.diagram svg')
    return this
  }

  setDepth(depth){
    let {choices, endings, depth:maxDepth} = this.book.stats,
        pct = depth % 1,
        inv = 1 - pct,
        tendrils = []

    // TODO: fade up page dots rather than pop-in
    //       have ending circles always present but dimmed in not yet reached

    if (pct==0 && depth<maxDepth){
      // draw white dots for all non-ending pages at this depth
      tendrils = _.filter(this.layout.nodes, ({depth:d, ending}) => d==depth && !ending)
                      .map( ({pt:{x,y}}) => `<circle fill="white" cx="${fmt(x)}"  cy="${fmt(y)}"  r="2" />`)
    }else if (pct){
      // draw all the incomplete branches (plus the white dots)
      tendrils = _.filter(this.layout.edges, b => b.depth!=depth && b.depth==_.ceil(depth)).map( branch => {
        let {src, srcCtrl, dstCtrl, dst} = branch.bezier,
            {depth, count, reversed, detached} = branch,
            color = reversed ? 'green' //rewind(1 - .6*count/endings.length)
                  : detached ? ffwd(1 - .6*count/endings.length)
                  : palette(1 - count/endings.length),
            dashes = reversed ? `stroke-width=".5" stroke-dasharray="1.5,1.5"` : '';

        // from the cabinet of De Casteljau
        let q0 = {x:inv*src.x+pct*srcCtrl.x, y:inv*src.y+pct*srcCtrl.y},
            q1 = {x:inv*srcCtrl.x+pct*dstCtrl.x, y:inv*srcCtrl.y+pct*dstCtrl.y},
            q2 = {x:inv*dstCtrl.x+pct*dst.x, y:inv*dstCtrl.y+pct*dst.y},
            r0 = {x:inv*q0.x+pct*q1.x, y:inv*q0.y+pct*q1.y},
            r1 = {x:inv*q1.x+pct*q2.x, y:inv*q1.y+pct*q2.y},
            s0 = {x:inv*r0.x+pct*r1.x, y:inv*r0.y+pct*r1.y};
        srcCtrl = q0
        dstCtrl = r0
        dst = s0

        return `<path class="tendril" stroke="${color}" ${dashes} d="${[
          `M ${fmt(src.x)} ${fmt(src.y)}`,
          `C ${fmt(srcCtrl.x)} ${fmt(srcCtrl.y)} ${fmt(dstCtrl.x)} ${fmt(dstCtrl.y)} ${fmt(dst.x)} ${fmt(dst.y)}`
        ].join(' ')}"/>` + `<circle fill="white" cx="${fmt(dst.x)}"  cy="${fmt(dst.y)}"  r="2" />`
      })
    }

    this.dom.find('.tendrils').html(tendrils.join(' '))
  }


  static layout(stats, {angles, radii, origin} = {}){
    angles = _.merge({min:180, run:180}, angles)
    radii = _.merge({min:30, perPg:4}, radii)
    origin = _.merge({x:0, y:0}, origin)

    let {pages, choices} = stats,
        {x:cX, y:cY} = origin

    // bounds and node-coord lookups for the radial geometry
    angles.max = angles.run - angles.min
    angles.perChoice = angles.run / pages.maxDepth
    radii.max = radii.min + pages.last*radii.perPg
    radii.mid = (radii.min + radii.max) / 2
    let da = _.mapValues(pages.depths, (depth, pg) => ({a:depth*angles.perChoice - angles.min, d:radii.max - parseInt(pg)*radii.perPg}) ),
        xy = _.mapValues(pages.depths, (depth, pg) => offset(cX,cY, da[pg].a, da[pg].d) ),
        start = offset(cX,cY, -angles.min, radii.mid),
        end = offset(cX,cY, angles.max, radii.mid),
        geom = {start, end, angles, radii, da, xy};

    // bezier edges for pg->pg 'choice' transitions
    let edges = _.flatMap(choices.futures, (count, pair) => {
      let branch = _.map(pair.split(','), _.toNumber),
          [srcPg, dstPg] = branch,
          src = geom.xy[srcPg],
          dst = geom.xy[dstPg],
          len = Math.sqrt(Math.pow(src.x-dst.x,2)+Math.pow(src.y-dst.y,2)),
          srcCtrl = offset(src.x, src.y, geom.da[srcPg].a+90, len*.4),
          dstCtrl = offset(dst.x, dst.y, geom.da[dstPg].a-90, len*.4),
          bezier = {src, srcCtrl, dstCtrl, dst},
          reversed = pages.depths[dstPg] - pages.depths[srcPg] != 1,
          detached = _.includes(stats.detached, srcPg),
          depth = choices.depths[branch];

      return {branch, bezier, depth, count, reversed, detached}
    })
    edges = _.orderBy(edges, ['depth', 'count'], ['asc', 'asc'])

    // point nodes for pages
    let nodes = _.map(pages.counts, (count, page) => {
      let pg = parseInt(page),
          {x,y} = geom.xy[pg],
          ending = _.includes(stats.endings.pages, pg),
          detached = _.includes(stats.detached, pg),
          pasts = ending ? stats.endings.pasts[pg] : 0;

      return {pg, count, futures:stats.pages.futures[pg], pasts, pt:{x,y}, depth:pages.depths[pg], ending, detached}
    })
    nodes = _.orderBy(nodes, ['ending', 'count'],['asc','desc'])

    return { geom, edges, nodes }
  }


  get svg(){
    let {book, legend} = this,
        {edges, nodes, geom:{angles, radii, start, end}} = this.layout,
        {choices, endings} = book.stats,
        sweep = angles.run>=180 ? 1 : 0,
        gClass = _.range(book.stats.depth+1).map(d=>"depth-"+d).join(' '),
        maxRings = _.min([endings.maxCount, 4]),
        nib = .75,
        style = `<style>
          .loop text{fill:#888; font:6.5px "Montserrat"; }
          .loop .sweep{ stroke:#151515; }
          .loop .marker{ stroke:#888; stroke-width:${nib}; }
        </style>`


    let rev = _.map(_.filter(edges, {reversed:true}), 'bezier'),
        boxes = rev.map(({src, srcCtrl, dstCtrl, dst}) =>
          bezierMinMax(src.x, src.y, srcCtrl.x, srcCtrl.y, dstCtrl.x, dstCtrl.y, dst.x, dst.y)
        )

    let DOT = 2,
        span = Math.max(radii.max, 100),
        top = -span - DOT,
        left = -span - DOT,
        bot = _.max(_.map(boxes, 'max.y').concat(0)) + (maxRings+1)*3,
        right = span + DOT,
        height = bot - top,
        width = right - left,
        key


    if (legend){
      // left -= nib
      // width += 2*nib
        // <text x="${nib + last.x + last.w}" y="32" text-anchor="end">Pg. ${last.number}</text>

      height += 20
      key = `<g class="markers">
        <path class="marker" d="M ${-radii.max + nib} 6 l 0 8" />
        <text x="${-radii.max}" y="23" text-anchor="start">Pg. 1</text>
        <path class="marker" d="M ${-radii.min + nib} 6 l 0 8" />
        <text x="${-radii.min + nib + DOT/2}" y="23" text-anchor="end">Pg. ${book.pages}</text>
      </g>`
    }

    return `<svg class="loop" xmlns="http://www.w3.org/2000/svg" viewBox="${left} ${top} ${width} ${height}">${
      _.flattenDeep([
        style,
        `<g class="${gClass}" fill="none">`,
          key,

          // background disc
          `<path class="sweep" stroke-width="${radii.max - radii.min}" d="M ${fmt(start.x)} ${fmt(start.y)} A ${radii.mid} ${radii.mid} 0 ${sweep} 1 ${fmt(end.x)} ${fmt(end.y)}" />`,

          // beziers for branching choices
          edges.map( ({bezier, depth, count, reversed, detached}) => {
            let {src,srcCtrl,dstCtrl,dst} = bezier,
                choicePct = count/endings.length,
                dashes = reversed ? `stroke-width=".5" stroke-dasharray="1.5,1.5"` : '',
                color = reversed ? 'green' //rewind(1 - .3 * choicePct)
                      : detached ? ffwd(1 - .6 * choicePct)
                                 : palette(1 - choicePct);
            return `<path class="branch depth-${depth}" stroke="${color}" ${dashes} d="${[
              `M ${fmt(src.x)} ${fmt(src.y)}`,
              `C ${fmt(srcCtrl.x)} ${fmt(srcCtrl.y)} ${fmt(dstCtrl.x)} ${fmt(dstCtrl.y)} ${fmt(dst.x)} ${fmt(dst.y)}`
            ].join(' ')}"/>`
          }),

          // circles for decision pages and endings
          nodes.map( ({pg, count, futures, pasts, pt, depth, ending, detached}, i) =>{
            let tooltip = `<title>pg. ${pg}</title>`,
                choicePct = futures/endings.length,
                // numRings = _.ceil(maxRings * pasts/endings.maxPast),
                numRings = _.ceil(maxRings * count/endings.maxCount),
                // numRings = _.ceil(maxRings * choicePct),
                stroke = detached ? ffwd : palette,
                fill = detached ? ffwd(1 - .6*choicePct)
                                 : palette(1 - choicePct);

            // // use the score if this book has graded endings
            // if (book.scores[pg]!==undefined){
            //   tooltip = `<title>${book.summaries[pg]}</title>`
            //   numRings = book.scores[pg]
            //   maxRings = 5
            // }

            if (ending){
              return `<circle class="ending depth-${depth}" fill="skyblue" cx="${fmt(pt.x)}" cy="${fmt(pt.y)}" r="2">${tooltip}</circle>`
            }

            // concentric circles for endings
            // if (ending) return _.range(numRings).map(i =>
            //   `<circle class="ending depth-${depth} ring-${i}"
            //            fill="${i ? 'rgba(0,0,0,.2)' : 'black'}"
            //            stroke="${stroke(.75 - .65*i/maxRings)}"
            //            cx="${fmt(pt.x)}" cy="${fmt(pt.y)}"
            //            r="${3 + 3*i}">${tooltip}</circle>`)

            // single dot for decision pages
            return `<circle class="page depth-${depth}" fill="${fill}" cx="${fmt(pt.x)}" cy="${fmt(pt.y)}" r="2">${tooltip}</circle>`
          }),

          // a container for the in-between choices beziers
          `<g class="tendrils"></g>`,

        '</g>',
      ]).join('')
    }</svg>`
  }



}
